import React from "react";

import cx from "classnames";

import { Spacer } from "../Grid/Spacer";
import Header from "../Header";
import { Icon } from "../Icon";
import { Text } from "../Text";

import styles from "./Card.scss";

/**
 * Type definition for the bar location.
 */
type BarLocation = "top" | "left";

/**
 * Type definition for the background options.
 */
type Background = "confetti" | "two-tone" | "blue-confetti" | "white-confetti";

/**
 * Props for the Card component.
 */
export interface CardProps {
  /**
   * Additional CSS class names to apply to the card
   */
  className?: string;
  /**
   * Handler for onClick event of card
   */
  onClick?: (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) => void;
  /**
   * Custom CSS class for the bar branding
   */
  barBrandingClass?: string;
  /**
   * Color of the bar. Accepts any valid CSS color
   */
  barColor?: string;
  /**
   * Location of the bar: "top" or "left"
   */
  barLocation?: BarLocation;
  /**
   * Whether to add a border to the card
   */
  withBorder?: boolean;
  /**
   * Background style of the card
   */
  background?: Background | null;
  /**
   * Whether to remove the box shadow
   */
  noShadow?: boolean;
  /**
   * Whether to remove padding from the card
   */
  noPadding?: boolean;
  /**
   * Description for the pinned reason
   */
  pinnedReason?: string;
  /**
   * Text for the pinned date (suggested format: "Pinned on XXXX XX, XXXX")
   */
  pinnedDateText?: string;
  /**
   * Whether to add a bottom margin
   */
  withMarginBottom?: boolean;
}

/**
 * Card component that wraps content in a styled container. It provides options
 * for customization such as borders, backgrounds, and pinned status.
 */
export function Card({
  children,
  className,
  onClick,
  barColor,
  barBrandingClass,
  barLocation,
  withBorder,
  background,
  noShadow,
  noPadding,
  pinnedReason,
  pinnedDateText,
  withMarginBottom,
  ...wrapperProps
}: React.PropsWithChildren<CardProps> &
  React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >) {
  const showBar = Boolean(barLocation);
  const showPinnedBanner = Boolean(pinnedDateText);

  const isButton = !!onClick;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    onClick?.(e);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.(e);
    }
  };

  const buttonProps = isButton
    ? {
        onClick: handleClick,
        onKeyPress: handleKeyPress,
        tabIndex: 0,
      }
    : {};

  const classNames = cx(
    styles.card,
    {
      [styles["card--with-border"]]: withBorder,
      [styles["card--no-shadow"]]: noShadow,
      [styles["card--no-padding"]]: noPadding,
      [styles["card--button"]]: isButton,
      [styles["confetti-background"]]: background === "confetti",
      [styles["two-tone-background"]]: background === "two-tone",
      [styles["card--pinned"]]: showPinnedBanner,
      [styles["white-confetti-background"]]: background === "white-confetti",
      [styles["blue-confetti-background"]]: background === "blue-confetti",
      [styles["card--margin-bottom"]]: withMarginBottom === true,
    },
    className
  );

  const barClassNames = cx(styles["card-bar"], barBrandingClass, {
    "primary-background": barColor === "primary",
    "secondary-background": barColor === "secondary",
    [styles["card-bar--top"]]: barLocation === "top",
    [styles["card-bar--left"]]: barLocation === "left",
  });

  const inlineStyle = barBrandingClass ? {} : { backgroundColor: barColor };

  if (showPinnedBanner) {
    return (
      <PinnedBannerCard
        pinnedDateText={pinnedDateText}
        pinnedReason={pinnedReason}
        classNames={classNames}
        buttonProps={buttonProps}
        wrapperProps={wrapperProps}
        showBar={showBar}
        barClassNames={barClassNames}
        inlineStyle={inlineStyle}
      >
        {children}
      </PinnedBannerCard>
    );
  }

  return (
    <div
      data-testid="card"
      className={classNames}
      {...buttonProps}
      {...wrapperProps}
    >
      {showBar && (
        <div
          data-testid="card-bar"
          className={barClassNames}
          style={inlineStyle}
        />
      )}
      {children}
    </div>
  );
}

function PinnedBannerCard(
  props: React.PropsWithChildren<{
    pinnedDateText?: string;
    pinnedReason?: string;
    classNames: string;
    buttonProps: Record<string, any>;
    wrapperProps: Record<string, any>;
    showBar: boolean;
    barClassNames: string;
    inlineStyle: React.CSSProperties | undefined;
  }>
) {
  const pinnedClassNames = cx(styles["pinned-banner"]);
  const pinnedDateClassNames = cx(styles["pinned-date"]);
  const pinnedIconClassNames = cx(styles["pinned-icon"]);
  const pinnedHeader = cx(styles["pinned-header"]);
  return (
    <div className={styles["pinned-banner-container"]}>
      <div className={pinnedClassNames} data-testid="pinned-banner">
        <div className={pinnedDateClassNames} data-testid="pinned-banner-date">
          <Icon
            className={pinnedIconClassNames}
            size={26}
            icon="push_pin"
            color={styles.systemBlue}
            data-testid="pinned-icon"
          />
          <Text
            size="paragraphSmall"
            color={styles.systemGray2}
            data-testid="pinned-date-text"
          >
            {props.pinnedDateText}
          </Text>
        </div>
        {Boolean(props.pinnedReason) && (
          <>
            <Spacer />
            <Header
              level="2"
              size="h5"
              data-testid="pinned-reason"
              className={pinnedHeader}
            >
              {props.pinnedReason}
            </Header>
          </>
        )}
      </div>
      <div
        data-testid="card"
        className={props.classNames}
        {...props.buttonProps}
        {...props.wrapperProps}
      >
        {props.showBar && (
          <div
            data-testid="card-bar"
            className={props.barClassNames}
            style={props.inlineStyle}
          />
        )}
        {props.children}
      </div>
    </div>
  );
}
